import React from "react";
import { ActivityIndicator, Pressable, Text, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import tw from "twrnc";

import { t } from "@/localization";
import { colors, control, radius } from "./theme";
import useCopy from "./useCopy";

// One button for every settings screen.
//   primary    the one main action of a view (brand, filled)
//   secondary  everything else that is a button (white, slate outline)
//   tertiary   low-emphasis actions inside content (brand text, no box)
//   danger     destructive actions (white, red outline); "danger-text" for inline
// Sizes: lg 48 (page actions), md 40 (inline/side by side), sm 32 (rows, cards).
const VARIANTS = {
  primary: {
    bg: colors.brand,
    bgPressed: colors.brandPressed,
    fg: colors.white,
    border: colors.brand,
  },
  secondary: {
    bg: colors.white,
    bgPressed: colors.surfaceStrong,
    fg: colors.slate,
    border: colors.slate,
  },
  tertiary: {
    bg: "transparent",
    bgPressed: colors.brandTint,
    fg: colors.brand,
    border: "transparent",
  },
  danger: {
    bg: colors.white,
    bgPressed: colors.dangerTint,
    fg: colors.danger,
    border: colors.danger,
  },
  "danger-text": {
    bg: "transparent",
    bgPressed: colors.dangerTint,
    fg: colors.danger,
    border: "transparent",
  },
};

const SIZES = {
  lg: { height: control.lg, px: 20, font: 15, icon: 20 },
  md: { height: control.md, px: 16, font: 14, icon: 18 },
  sm: { height: control.sm, px: 12, font: 13, icon: 16 },
};

const Button = ({
  text,
  onPress,
  variant = "secondary",
  size = "lg",
  icon,
  iconRight,
  loading = false,
  disabled = false,
  fullWidth = true,
  style,
  accessibilityLabel,
  testID,
}) => {
  const v = VARIANTS[variant] || VARIANTS.secondary;
  const s = SIZES[size] || SIZES.lg;
  // A real boolean: Android crashes on non-boolean accessibilityState values.
  const isDisabled = !!(disabled || loading);
  const fg = isDisabled && variant !== "tertiary" ? colors.subtle : v.fg;
  const inline = variant === "tertiary" || variant === "danger-text";

  return (
    <Pressable
      onPress={isDisabled ? undefined : onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel || text}
      accessibilityState={{ disabled: isDisabled, busy: !!loading }}
      // react-native-web ignores accessibilityState; aria-busy reaches the DOM.
      aria-busy={!!loading}
      testID={testID}
      style={({ pressed, hovered }) => [
        tw`flex-row items-center justify-center`,
        {
          minHeight: s.height,
          paddingHorizontal: inline ? 8 : s.px,
          borderRadius: radius.control,
          borderWidth: 1,
          borderColor: v.border,
          backgroundColor:
            (pressed || hovered) && !isDisabled ? v.bgPressed : v.bg,
        },
        isDisabled &&
          !inline && {
            backgroundColor: colors.disabledBg,
            borderColor: colors.disabledBg,
          },
        fullWidth && !inline ? tw`w-full` : tw`self-start`,
        style,
      ]}
    >
      {loading ? (
        <ActivityIndicator size="small" color={fg} style={tw`mr-2`} />
      ) : icon ? (
        <Ionicons name={icon} size={s.icon} color={fg} style={tw`mr-2`} />
      ) : null}
      <Text
        numberOfLines={1}
        style={{
          color: fg,
          fontSize: s.font,
          lineHeight: s.font + 5,
          fontWeight: "600",
        }}
      >
        {text}
      </Text>
      {!!iconRight && (
        <Ionicons
          name={iconRight}
          size={s.icon}
          color={fg}
          style={tw`ml-1.5`}
        />
      )}
    </Pressable>
  );
};

// A button that copies `value`, then shows "Copied" with a check for a moment
// (plus a toast). onCopy runs after copying (e.g. analytics).
export const CopyButton = ({
  value,
  text = t("Copy"),
  copiedText = t("Copied"),
  toast = t("Copied"),
  toastDetail,
  icon = "copy-outline",
  onCopy,
  ...props
}) => {
  const { copy, isCopied } = useCopy();
  const copied = isCopied();
  return (
    <Button
      {...props}
      text={copied ? copiedText : text}
      icon={copied ? "checkmark" : icon}
      onPress={async () => {
        await copy(value, "default", { toast, detail: toastDetail });
        onCopy?.();
      }}
    />
  );
};

// Buttons side by side (wrap on narrow screens), e.g. [Cancel] [Save].
export const ButtonRow = ({ children, style, align = "end" }) => (
  <View
    style={[
      tw`flex-row flex-wrap gap-3`,
      align === "end"
        ? tw`justify-end`
        : align === "start"
        ? tw`justify-start`
        : tw`justify-between`,
      style,
    ]}
  >
    {children}
  </View>
);

// Buttons stacked full width, 12px apart.
export const ButtonStack = ({ children, style }) => (
  <View style={[tw`gap-3`, style]}>{children}</View>
);

export default Button;
